import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';

import CalendarIcon from '@/assets/svg/bx-calendar-alt.svg?react';
import { Button } from '@/components/buttons';
import Calendar from '@/components/Calendar';
import Popover, { PopoverContent, PopoverTrigger } from '@/components/Popover';
import { USDateViewFormat } from '@/constants/formats';
import { cn } from '@/lib/utils';

export type DateRangePickerProps = {
  className?: string;
  disabled?: boolean;
  align?: 'start' | 'center' | 'end';
  value?: DateRange | undefined;
  onValueChange?: (value: DateRange | undefined) => void;
};

const DateRangePicker = ({
  className,
  disabled,
  align = 'start',
  value,
  onValueChange,
}: DateRangePickerProps) => {
  const [open, setOpen] = useState(false);

  const handleDateRangeChange = (dateRange: DateRange) => {
    onValueChange?.(dateRange);
  };

  const handlePresetClick = (days: number) => () => {
    onValueChange?.({
      from: dayjs().subtract(days, 'd').startOf('d').toDate(),
      to: dayjs().endOf('d').toDate(),
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} data-testid="date-range-picker">
      <PopoverTrigger
        render={
          <Button
            className={cn(
              'justify-start',
              'border border-gray-600 font-normal',
              'hover:border-gray-900 hover:bg-white',
              'active:border-green-500 active:bg-white',
              'focus:border-green-500 focus:bg-white focus:ring-1 focus:ring-green-500/50 focus:outline-none focus-visible:bg-white',
              'aria-expanded:border-green-500 aria-expanded:ring-1 aria-expanded:ring-green-500/50 aria-expanded:ring-offset-0',
              className,
            )}
            variant="ghost"
            shape="square"
            size="large"
            disabled={disabled}
          >
            <CalendarIcon data-icon="inline-start" />
            <span className="flex gap-4 px-2">
              <span>{dayjs(value?.from).format(USDateViewFormat)}</span>
              <span>-</span>
              <span>{dayjs(value?.to).format(USDateViewFormat)}</span>
            </span>
          </Button>
        }
      />
      <PopoverContent className="w-fit p-0 shadow-2xl" align={align}>
        <div className="flex flex-col gap-2">
          <Calendar
            mode="range"
            numberOfMonths={2}
            captionLayout="dropdown-years"
            defaultMonth={dayjs(value?.from).toDate()}
            disabled={{ after: dayjs().endOf('d').toDate() }}
            startMonth={dayjs(new Date(2023, 0, 1)).toDate()}
            endMonth={dayjs().toDate()}
            required
            showOutsideDays={false}
            selected={value}
            onSelect={handleDateRangeChange}
          />
          <div className="flex gap-2 border-t p-3">
            <Button onClick={handlePresetClick(30)}>
              <Trans>Last 30 Days</Trans>
            </Button>
            <Button onClick={handlePresetClick(90)}>
              <Trans>Last 90 Days</Trans>
            </Button>
            <Button onClick={handlePresetClick(180)}>
              <Trans>Last 180 Days</Trans>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
